// Invariants — cross-entity rules that the engine enforces but a single
// Zod schema can't express. Every function takes the resolved Recipe (loaded
// upstream from the org's recipe_slug) and the entity to validate.
//
// Each function throws ValidationError with field-level `details`. Callers
// catch and map to HTTP. Errors aggregate: a single call reports every
// problem it finds, not just the first one — saves round-trips when a
// client posts a malformed payload.
import { ConflictError, ValidationError, rutSchema } from "@bitacorapro/shared";
/**
 * For each incoming party whose recipe role declares a non-form `source`,
 * fill its `idDoc` from the auth context (user / org). Returns a new
 * array; never mutates input. Roles with `source === "form"` (or no
 * source) pass through untouched.
 *
 * Run this BEFORE validateParties on every PUT/sign that writes parties:
 *   const populated = populateSourcedPartyIds(recipe, body.parties, ctx);
 *   validateParties(recipe, populated);
 *
 * If the source is declared but the context value is missing, idDoc is
 * cleared (not left as whatever the client sent) so validateParties
 * surfaces a clear "RUT required" error — the user needs to fill their
 * profile. A sourced role's idDoc must never be settleable by client
 * input: leaving a client-supplied value as-is here would let anyone
 * without a profile RUT set stamp an arbitrary RUT onto a signed,
 * legally meaningful document for that role.
 */
export function populateSourcedPartyIds(recipe, parties, context) {
    return parties.map((party) => {
        const role = recipe.partyRoles.find((r) => r.key === party.role);
        if (!role)
            return party;
        // ADR-0040 — a role declaring idDocument:"none" carries no identity
        // document, ever, regardless of `source`. Strip rather than skip:
        // validateParties enforces nothing on "none" roles, so a skipped
        // value (client-supplied, or a session RUT stamped by the branches
        // below) would persist into an immutable, legally meaningful acta
        // that the recipe says carries no RUT.
        if (role.idDocument === "none") {
            return { ...party, idDoc: undefined };
        }
        if (!role.source || role.source === "form")
            return party;
        if (role.source === "session_user") {
            return { ...party, idDoc: context.userRut ?? undefined };
        }
        if (role.source === "session_organization") {
            return { ...party, idDoc: context.organizationRut ?? undefined };
        }
        return party;
    });
}
function record(issues, path, message) {
    (issues[path] ??= []).push(message);
}
function throwIfAny(issues, message) {
    if (Object.keys(issues).length > 0) {
        throw new ValidationError({
            code: "INVARIANT_VIOLATION",
            message,
            details: issues,
        });
    }
}
/** The report's `reportType` must appear in the recipe. */
export function validateReportType(recipe, reportType) {
    const known = new Set(recipe.reportTypes.map((t) => t.key));
    if (!known.has(reportType)) {
        throw new ValidationError({
            code: "UNKNOWN_REPORT_TYPE",
            message: `Recipe "${recipe.slug}" has no reportType "${reportType}"`,
            details: { reportType: ["unknown for this recipe"] },
        });
    }
}
/** A single finding's `category` and `severity` must come from the recipe. */
export function validateFinding(recipe, finding) {
    const issues = {};
    const categoryKeys = new Set(recipe.findingCategories.map((c) => c.key));
    if (!categoryKeys.has(finding.category)) {
        record(issues, "category", `unknown category "${finding.category}" for recipe ${recipe.slug}`);
    }
    const severityKeys = new Set(recipe.severities.map((s) => s.key));
    if (!severityKeys.has(finding.severity)) {
        record(issues, "severity", `unknown severity "${finding.severity}" for recipe ${recipe.slug}`);
    }
    throwIfAny(issues, "Finding does not match recipe");
}
/**
 * A finding's asset-scoped `observationStatus` must come from the
 * recipe's `observationStatuses` vocabulary (ADR-0058) AND be settable
 * by the caller's actor kind (postventa share-link feature, council
 * review). Distinct from `delegationStatus` (provider-delegation,
 * ADR-0074) — see the schema comment on `observationStatusSchema` for
 * why they aren't shared.
 *
 * Returns the resolved status entry rather than void — this is the
 * SINGLE choke point every caller derives `terminal` from (e.g. to
 * stamp `finding_events.to_status_terminal`). No other file should
 * read `recipe.observationStatuses` to compute terminality; a second
 * ad-hoc derivation site is exactly the "forgot to call the guard"
 * class of bug the council review was trying to close.
 *
 * `actor` defaults to "session_user" so every pre-existing call site
 * keeps its current behavior unchanged.
 *
 * Note the signature asymmetry with `validateDelegationTransition`,
 * which takes no `Recipe`: this vocabulary is recipe-declared and only
 * knowable at runtime, that one is engine-owned. See ADR-0074 §5.
 */
export function resolveObservationStatus(recipe, status, actor = "session_user") {
    const found = recipe.observationStatuses.find((s) => s.key === status);
    if (!found) {
        throw new ValidationError({
            code: "INVARIANT_VIOLATION",
            message: "Observation status does not match recipe",
            details: {
                observationStatus: [`unknown observation status "${status}" for recipe ${recipe.slug}`],
            },
        });
    }
    if (!found.settableBy.includes(actor)) {
        throw new ValidationError({
            code: "INVARIANT_VIOLATION",
            message: "Observation status does not match recipe",
            details: {
                observationStatus: [`status "${status}" is not settable by actor "${actor}"`],
            },
        });
    }
    return found;
}
/**
 * Maps an authenticated membership role to the observation-status actor
 * lane used by `resolveObservationStatus`. Looked up from
 * `recipe.memberRoles[].observationStatusActor` (fallback
 * "session_user" for a DECLARED role without an explicit lane).
 *
 * A role absent from `recipe.memberRoles` entirely fails CLOSED
 * (council 2026-08-31, cross-recipe token hygiene): a JWT role minted
 * under a different recipe context, or a typo'd seed role, must not
 * silently receive the most privileged lane. Recorded explicitly as
 * NOT a mitigation of #0567 — every role postventa declares resolves
 * through the found-branch below, so the real #0567 fix is the
 * "assigning_party" node ceiling on the sensitive destination
 * (ADR-0129, Option A), not this guard.
 */
export function resolveMemberRoleActor(recipe, role) {
    const found = recipe.memberRoles.find((r) => r.key === role);
    if (!found) {
        throw new ValidationError({
            code: "INVARIANT_VIOLATION",
            message: "Observation status does not match recipe",
            details: {
                observationStatus: [
                    `role "${role}" is not declared in recipe ${recipe.slug}'s memberRoles`,
                ],
            },
        });
    }
    return found.observationStatusActor ?? "session_user";
}
/**
 * El rol al que se invita al "dueño" de una unidad — la contraparte
 * externa que observa sobre su propio asset (ADR-0124).
 *
 * Deliberadamente un predicado sobre DOS primitivos y no una clave:
 * `observationStatusActor: "asset_party"` (es la contraparte, no
 * personal del tenant) + `scopeType: "asset"` (su alcance es una unidad,
 * no un proyecto ni la organización). `POST /assets/:id/owner-invitation`
 * lo consume para decidir a qué rol invita; una receta que no declara
 * ninguno —rental, facility— hace que esa ruta responda 422 sin tocar
 * ninguna fila, que es exactamente el comportamiento buscado: activar
 * unidades es una capacidad de la vertical, no del endpoint.
 *
 * Filtra `retired: true`. Ésa es la diferencia con
 * `resolveMemberRoleActor` de más arriba, que NO filtra (#0663): aquél
 * responde "esta membresía viva, ¿en qué carril cae?" y tiene que seguir
 * resolviendo claves legacy; éste responde "¿a qué rol invito hoy?", y
 * una clave que nadie vuelve a emitir no es respuesta. En
 * broker_postventa la distinción es la que evita invitar al propietario
 * como `store_manager` legacy — la clave que pertenece a los cuatro
 * conjuntos de capacidad de api-types y que ADR-0090 sacó de la vertical
 * precisamente por eso.
 *
 * Devuelve null cuando no hay ninguno. El superRefine de recipeSchema
 * ya asegura que nunca haya más de uno vivo; el `.find()` de acá es
 * consistente con esa garantía, y el llamador valida la unicidad otra
 * vez (defensa en profundidad) porque las recetas no se parsean.
 */
export function resolveAssetPartyMemberRole(recipe) {
    return (recipe.memberRoles.find((r) => r.retired !== true && r.observationStatusActor === "asset_party" && r.scopeType === "asset") ?? null);
}
/**
 * Cuántos memberRoles VIVOS cumplen el predicado de
 * `resolveAssetPartyMemberRole`. Existe para que el llamador pueda
 * distinguir 0 (422 RECIPE_HAS_NO_OWNER_ROLE) de >1 (422
 * RECIPE_OWNER_ROLE_AMBIGUOUS) sin re-escribir el predicado — dos copias
 * del mismo filtro es exactamente cómo divergen.
 */
export function countAssetPartyMemberRoles(recipe) {
    return recipe.memberRoles.filter((r) => r.retired !== true && r.observationStatusActor === "asset_party" && r.scopeType === "asset").length;
}
/**
 * Every party's role must come from the recipe and any required ID document
 * (e.g. RUT for residential rentals) must validate.
 */
export function validateParties(recipe, parties) {
    const issues = {};
    const byKey = new Map(recipe.partyRoles.map((r) => [r.key, r]));
    for (let i = 0; i < parties.length; i++) {
        const party = parties[i];
        const role = byKey.get(party.role);
        if (!role) {
            record(issues, `parties.${i}.role`, `unknown party role "${party.role}"`);
            continue;
        }
        // Name is recipe-driven (ADR-0040). The base partySchema makes `name`
        // optional; the requirement is re-enforced here per role. Roles that
        // declare nameRequired:false (facility's stroke-only signers) may sign
        // with no typed name; every other role keeps the name requirement.
        if (role.nameRequired !== false && (party.name === undefined || party.name.trim() === "")) {
            record(issues, `parties.${i}.name`, "name required for this role");
        }
        if (role.idDocument === "rut") {
            if (party.idDoc === undefined) {
                record(issues, `parties.${i}.idDoc`, "RUT required for this role");
            }
            else {
                const result = rutSchema.safeParse(party.idDoc);
                if (!result.success) {
                    for (const issue of result.error.issues) {
                        record(issues, `parties.${i}.idDoc`, issue.message);
                    }
                }
            }
        }
        else if (role.idDocument === "none") {
            // Nothing to enforce here. The validator can stay permissive because
            // populateSourcedPartyIds strips idDoc on every "none" role before
            // any parties write reaches validation (ADR-0040 data minimization).
        }
        else if (party.idDoc === undefined) {
            record(issues, `parties.${i}.idDoc`, `${role.idDocument} required for this role`);
        }
    }
    throwIfAny(issues, "Parties do not match recipe");
}
/**
 * For each `required` party role in the recipe, `parties` has at least one
 * entry. Used at sign time and on report finalization.
 */
export function validateRequiredParties(recipe, parties) {
    const issues = {};
    const provided = new Set(parties.map((p) => p.role));
    for (const role of recipe.partyRoles) {
        if (role.required && !provided.has(role.key)) {
            record(issues, `parties.${role.key}`, "required party role missing");
        }
    }
    throwIfAny(issues, "Required parties missing");
}
/**
 * ADR-0065 (enmendado 2026-09-12) — gates POST /assets/:id/observations.
 * When the recipe declares `observationsRequireAssetStatusIn`, a loose
 * observation can only be created for an asset whose current
 * `asset_status` is one of the listed values. The caller resolves that
 * status (a DB read) and passes it in — this function only enforces the
 * rule, it does not query anything, matching every other invariant in
 * this file.
 *
 * Recipes that don't declare the field (undefined) impose no gate — any
 * asset can receive a loose observation at any time.
 *
 * Fails closed on an absent status: an asset that never went through
 * activation has no `asset_status` at all, and "no value" must never
 * read as "allowed". This is the half of the rule that the previous
 * milestone-shaped gate got for free (no report row = no signed report)
 * and that has to be explicit now.
 *
 * Recipe carries policy (which statuses open the gate); engine carries
 * mechanism (the membership check). Never a `recipe.slug` branch.
 */
export function assertObservationEntryAllowed(recipe, asset) {
    const allowed = recipe.observationsRequireAssetStatusIn;
    if (!allowed || allowed.length === 0)
        return;
    const current = asset?.assetStatus ?? null;
    if (current === null || !allowed.includes(current)) {
        throw new ConflictError({
            code: "ASSET_NOT_ACTIVATED",
            message: `Recipe "${recipe.slug}" only accepts observations for an asset whose status is one of [${allowed.join(", ")}]; this asset is "${current ?? "(sin estado)"}"`,
        });
    }
}
/**
 * Validate signatures at sign time:
 *   - every signature.role is a known recipe.signatureRoles entry
 *   - every recipe.signatureRoles entry has a matching signature
 *   - declinable=false roles cannot have declined=true signatures
 *   - every signature.role has a corresponding party in `parties`
 */
export function validateSignatures(recipe, parties, signatures) {
    const issues = {};
    const sigRolesByKey = new Map(recipe.signatureRoles.map((r) => [r.partyRole, r]));
    const partyRoleKeys = new Set(parties.map((p) => p.role));
    for (let i = 0; i < signatures.length; i++) {
        const sig = signatures[i];
        const sigRole = sigRolesByKey.get(sig.role);
        if (!sigRole) {
            record(issues, `signatures.${i}.role`, `unknown signature role "${sig.role}"`);
            continue;
        }
        if (sig.declined && !sigRole.declinable) {
            record(issues, `signatures.${i}.declined`, `role "${sig.role}" cannot decline to sign`);
        }
        if (!partyRoleKeys.has(sig.role)) {
            record(issues, `signatures.${i}.role`, `no party with role "${sig.role}" — every signer must be a party`);
        }
    }
    // Every signatureRole the recipe declares must be present in `signatures`.
    // A missing signature is treated separately from one with the wrong role.
    const provided = new Set(signatures.map((s) => s.role));
    for (const role of recipe.signatureRoles) {
        if (!provided.has(role.partyRole)) {
            record(issues, `signatures.${role.partyRole}`, "signature missing for this role");
        }
    }
    throwIfAny(issues, "Signatures do not match recipe");
}
/**
 * Validate the constancia "no firmó" payload (ADR-0028 / H-03) against
 * the signatures and recipe. Call this AFTER validateSignatures has
 * accepted the declined signature event.
 *
 * Invariants enforced:
 *   - If no signature is declined, `constancia` must be undefined.
 *     (A constancia without a declined signer would be meaningless.)
 *   - If a signature is declined AND the recipe's signatureRole has
 *     `declineReasonCapture`, `constancia` is required.
 *   - constancia.role must match exactly one declined signature's role.
 *   - When constancia.motivo === "otro", the matching declined
 *     signature must carry a non-empty declineNote (≥ 10 chars; the
 *     Zod schema already caps at 240).
 *
 * `constancia` is the payload incoming from the route handler; the
 * persistence into `reports.constancia_no_firma_*` happens at the route
 * boundary inside the same DB transaction as the `reports.signedAt`
 * UPDATE (security S-1 / S-2).
 */
export function validateConstancia(recipe, signatures, constancia) {
    const issues = {};
    const declinedSigs = signatures.filter((s) => s.declined);
    if (declinedSigs.length === 0) {
        if (constancia !== undefined) {
            record(issues, "constancia", "constancia present but no signature was declined");
        }
        throwIfAny(issues, "Constancia does not match signatures");
        return;
    }
    // Recipe roles that require declineReasonCapture among the declined sigs.
    const sigRolesByKey = new Map(recipe.signatureRoles.map((r) => [r.partyRole, r]));
    const capturingRoles = declinedSigs.filter((s) => {
        const role = sigRolesByKey.get(s.role);
        return role?.declineReasonCapture !== undefined;
    });
    if (capturingRoles.length > 0 && constancia === undefined) {
        record(issues, "constancia", "constancia required when declining a signature with declineReasonCapture");
        throwIfAny(issues, "Constancia does not match signatures");
        return;
    }
    if (constancia === undefined) {
        // Declined signatures present, but none require capture — legacy
        // path (residential's arrendatario). Constancia stays optional.
        return;
    }
    // The constancia.role must match one of the declined signers.
    const declinedRole = declinedSigs.find((s) => s.role === constancia.role);
    if (!declinedRole) {
        record(issues, "constancia.role", `no declined signature with role "${constancia.role}"`);
    }
    else if (constancia.motivo === "otro") {
        // motivo='otro' requires the signer's declineNote to carry the
        // explanation (≥10 chars; Zod max is 240). Stored on the signature
        // itself rather than as a 4th column on `reports`.
        const note = declinedRole.declineNote ?? "";
        if (note.length < 10) {
            record(issues, "signatures.declineNote", 'motivo="otro" requires declineNote of at least 10 characters');
        }
    }
    throwIfAny(issues, "Constancia does not match signatures");
}
//# sourceMappingURL=invariants.js.map